// src/App.js
import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ProgramProvider } from "./context/ProgramContext";
import Sidebar from "./components/sidebars/athleteSidebar"; // Kept for reference
import RoleSidebar from "./components/sidebars/RoleSidebar"; // Your active sidebar
import InstallPrompt from "./components/InstallPrompt";
import { supabase } from "./supabaseClient";

// --- Import Pages (No changes to imports) ---
import NewDashboard from "./pages/athlete/NewDashboard";
import SkillWorkouts from "./pages/athlete/SkillWorkouts";
import SCWorkouts from "./pages/athlete/SCworkouts";
import WeeklyWorkouts from "./pages/athlete/IndividualSCWorkout";
import CombineScores from "./pages/athlete/CombineScores";
import Marketplace from "./pages/athlete/Marketplace";
import Profile from "./pages/athlete/Profile";
import SWOT from "./pages/athlete/SWOT";
import TryoutRegistration from "./pages/athlete/TryoutRegistration";
import Login from "./pages/login";
import CoachPage from "./pages/coach/CoachDashboard";
import CoachProfile from "./pages/coach/Profile";
import ProgramCombineScores from "./pages/coach/CombineScores";
import CoachSignup from "./pages/coach/CoachSignup";
import CoachTeamResources from "./pages/coach/TeamResources";
import AdminPage from "./pages/admin/AdminDashboard";
import AdminCombineScores from "./pages/admin/CombineScores";
import CreateProgramPage from "./pages/admin/CreateProgram";
import EditTeam from "./pages/admin/EditTeam";
import CreateTeamPage from "./pages/admin/CreateTeam";
import CreateTrainer from "./pages/skillsTrainer/CreateTrainer";
import AthleteDevelopment from "./pages/admin/AthleteDev";
import TeamInformation from "./pages/admin/TeamInformation";
import Tryouts from "./pages/admin/AdminTryoutDashboard";
import Audience from "./pages/admin/FollowedUsers";
import AdminTryoutDashboard from "./pages/admin/AdminTryoutDashboard";
import CreateTryout from "./pages/admin/CreateTryout";
import EditTryout from "./pages/admin/EditTryout";
import RegisteredAthletes from "./pages/admin/RegisteredAthletes";
import AdminProfile from "./pages/admin/Profile";
import ProgramUpgrade from "./pages/admin/ProgramUpgrade";
import PasswordRecovery from "./pages/PasswordRecovery";
import CreateAccount from "./pages/CreateAccount";
import PaymentSuccess from "./pages/PaymentSuccess";
import SWOTPage from "./pages/coach/SWOTPage";
import SkillsTrainerSwotHub from "./pages/skillsTrainer/SwotHub";
import SkillsTrainerDashboard from "./pages/skillsTrainer/Dashboard";
import SkillsTrainerProfile from "./pages/skillsTrainer/Profile";
import Logout from "./pages/Logout";
import Documentation from "./pages/guide/Documentation";
import AthleteOnBoarding from "./pages/guide/athlete/AthleteDoc";
import AdminOnBoarding from "./pages/guide/administrator/adminDoc";
import CoachOnBoarding from "./pages/guide/coach/coachDoc";
import SchedulePage from "./pages/Schedule";
import LandingPage from "./pages/landingPages/LandingPage";
import ProgramLanding from "./pages/landingPages/Programs";
import AthleteLanding from "./pages/landingPages/Athletes";
import PricingPage from "./pages/landingPages/Pricing";
import CaseStudiesPage from "./pages/landingPages/CaseStudies";
import AboutPage from "./pages/landingPages/About";
import WhyNuvio from "./pages/landingPages/WhyNuvio";
import FeaturesPage from "./pages/landingPages/Features";
import OffseasonProgramConfirm from "./pages/athlete/OffseasonProgramConfirm";
import OffseasonSkillGeneratorConfirm from "./pages/athlete/OffseasonSkillGeneratorConfirm";



function App() {
  const [user, setUser] = useState(null);

  const getPublicEntryRoute = () => {
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone;
    return isStandalone ? "/login" : "/home";
  };

  // Check if user is already logged in
  useEffect(() => {
    const session = supabase.auth.getSession().then(({ data }) => {
      if (data.session) setUser(data.session.user);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) setUser(session.user);
      else setUser(null);
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  const PrivateRoute = ({ children, roles }) => {
    if (!user) return <Navigate to={getPublicEntryRoute()} replace />;
    if (roles && roles.length > 0) {
      return <RoleGuard user={user} roles={roles}>{children}</RoleGuard>;
    }
    return children;
  };

  return (
    <ProgramProvider userId={user?.id || null}>
      <Router>
        <div className="flex h-screen min-h-screen h-[100dvh] min-h-[100dvh] overflow-hidden bg-gray-50">

          {/* 1. NAVIGATION BARS 
            This component contains the FIXED Top and Bottom bars.
            They sit 'above' the main content visually (z-index).
          */}
          {user && <RoleSidebar user={user} />}

          {/* 2. MAIN CONTENT WRAPPER (THE FIX)
            - pt-[calc(...)]: Pushes content DOWN based on the Top Bar height + iPhone Notch.
            - pb-[calc(...)]: Pushes content UP based on Bottom Nav + iPhone Home Bar.
            - overflow-y-auto: Ensures scrolling happens INSIDE this container.
          */}
          <main
            className={`
              flex-1
              min-w-0  /* <--- THIS IS THE MAGIC FIX for the 'wide' horizontal scrolling issue */
              relative
              w-full
              h-full
              overflow-y-auto
              overflow-x-hidden /* <--- Ensures no horizontal scrolling */
              px-4
              md:p-6
              ${user ? "pt-[calc(4rem+env(safe-area-inset-top))] pb-16 md:pt-6 md:pb-6" : "pt-0 pb-0"}
            `}
          >
            <Routes>
              {/* Landing Pages Route */}
              <Route path="/home" element={<LandingPage />} />
              <Route path="/home/athletes" element={<AthleteLanding />} />
              <Route path="/home/programs" element={<ProgramLanding />} />
              <Route path="/home/pricing" element={<PricingPage />} />
              <Route path="/home/features" element={<FeaturesPage />} />
              <Route path="/home/case-studies" element={<CaseStudiesPage />} />
              <Route path="/home/about" element={<AboutPage />} />
              <Route path="/home/why-nuvio" element={<WhyNuvio />} />
              <Route path="/blog" element={<LandingPage />} />

              {/* Public Route */}
              <Route path="/login" element={<Login />} />
              <Route path="/logout" element={<Logout />} />
              <Route path="/create-account" element={<CreateAccount />} />
              <Route path="/password-recovery" element={<PasswordRecovery />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/create-program" element={<CreateProgramPage />} />
              <Route path="/create-team" element={<CreateTeamPage />} />
              <Route path="/skills-trainer/create-program" element={<PrivateRoute roles={["SkillsTrainer"]}><CreateTrainer /></PrivateRoute>} />
              <Route path="/schedule" element={<PrivateRoute roles={["Athlete", "SkillsTrainer", "Administrator"]}><SchedulePage /></PrivateRoute>} />

              {/* Documentation Routes */}
              <Route path="/documentation" element={<Documentation />} />
              <Route path="/athlete-onboarding" element={<AthleteOnBoarding />} />
              <Route path="/admin-onboarding" element={<AdminOnBoarding />} />
              <Route path="/coach-onboarding" element={<CoachOnBoarding />} />

              {/* Athlete Routes */}
              <Route path="/" element={<PrivateRoute roles={["Athlete"]}><NewDashboard userId={user?.id} /></PrivateRoute>} />
              <Route path="/skills-workouts" element={<PrivateRoute roles={["Athlete"]}><SkillWorkouts userId={user?.id} /></PrivateRoute>} />
              <Route path="/sc-workouts" element={<PrivateRoute roles={["Athlete"]}><SCWorkouts userId={user?.id} /></PrivateRoute>} />
              <Route path="/weekly-workouts" element={<PrivateRoute roles={["Athlete"]}><WeeklyWorkouts userId={user?.id} /></PrivateRoute>} />
              <Route path="/combine-scores" element={<PrivateRoute roles={["Athlete"]}><CombineScores userId={user?.id} /></PrivateRoute>} />
              <Route path="/marketplace" element={<PrivateRoute roles={["Athlete"]}><Marketplace userId={user?.id} /></PrivateRoute>} />
              <Route path="/athlete/program/:programId/tryouts" element={<PrivateRoute roles={["Athlete"]}><TryoutRegistration userId={user?.id} /></PrivateRoute>} />
              <Route path="/profile" element={<PrivateRoute roles={["Athlete"]}><Profile userId={user?.id} /></PrivateRoute>} />
              <Route path="/athlete/offseason-program" element={<PrivateRoute roles={["Athlete"]}><OffseasonProgramConfirm userId={user?.id} /></PrivateRoute>} />
              <Route path="/athlete/offseason-skill-generator" element={<PrivateRoute roles={["Athlete"]}><OffseasonSkillGeneratorConfirm userId={user?.id} /></PrivateRoute>} />
              <Route path="/swot" element={<PrivateRoute roles={["Athlete"]}><SWOT userId={user?.id} /></PrivateRoute>} />

              {/* Coach Routes */}
              <Route path="/coach" element={<PrivateRoute roles={["Coach"]}><CoachPage userId={user?.id} /></PrivateRoute>} />
              <Route path="/coach/profile" element={<PrivateRoute roles={["Coach"]}><CoachProfile userId={user?.id} /></PrivateRoute>} />
              <Route path="/coach/team-resources" element={<PrivateRoute roles={["Coach"]}><CoachTeamResources userId={user?.id} teamId={user?.team_id} /></PrivateRoute>} />
              <Route path="/coach/invite" element={<PrivateRoute roles={["Coach"]}><CoachSignup userId={user?.id} teamId={user?.team_id} /></PrivateRoute>} />
              <Route path="/coach/combine-scores" element={<PrivateRoute roles={["Coach"]}><ProgramCombineScores userId={user?.id} /></PrivateRoute>} />
              <Route path="/coach/swot/:athleteId" element={<SWOTPage />} />

              {/* Admin Routes */}
              <Route path="/admin" element={<PrivateRoute roles={["Administrator", "SkillsTrainer"]}><AdminPage userId={user?.id} /></PrivateRoute>} />
              <Route path="/admin/program/:programId/followed-users" element={<PrivateRoute roles={["Administrator", "SkillsTrainer"]}><Audience userId={user?.id} /></PrivateRoute>} />
              <Route path="/admin/program/:programId/team/:teamId/dashboard" element={<PrivateRoute roles={["Administrator", "SkillsTrainer"]}><AdminPage userId={user?.id} /></PrivateRoute>} />
              <Route path="/admin/program/:programId/team/:teamId/combine-scores" element={<PrivateRoute roles={["Administrator", "SkillsTrainer"]}><AdminCombineScores userId={user?.id} /></PrivateRoute>} />
              <Route path="/admin/team-information/:teamId" element={<PrivateRoute roles={["Administrator", "SkillsTrainer"]}><TeamInformation userId={user?.id} /></PrivateRoute>} />
              <Route path="/admin/program/:programId/team/:teamId/edit" element={<PrivateRoute roles={["Administrator", "SkillsTrainer"]}><EditTeam userId={user?.id} /></PrivateRoute>} />
              <Route path="/admin/tryouts/:teamId" element={<PrivateRoute roles={["Administrator", "SkillsTrainer"]}><Tryouts userId={user?.id} /></PrivateRoute>} />
              <Route path="/admin/athlete-development" element={<PrivateRoute roles={["Administrator", "SkillsTrainer"]}><AthleteDevelopment userId={user?.id} /></PrivateRoute>} />
              <Route path="/admin/program/:programId/team/:teamId/profile" element={<PrivateRoute roles={["Administrator", "SkillsTrainer"]}><AdminProfile userId={user?.id} /></PrivateRoute>} />
              <Route path="/admin/program/:programId/upgrade" element={<PrivateRoute roles={["Administrator", "SkillsTrainer"]}><ProgramUpgrade /></PrivateRoute>} />
              <Route path="/admin/program/:programId/team/:teamId/tryouts" element={<PrivateRoute roles={["Administrator", "SkillsTrainer"]}><AdminTryoutDashboard userId={user?.id} /></PrivateRoute>} />
              <Route path="/admin/teams/:teamId/tryouts/create" element={<PrivateRoute roles={["Administrator", "SkillsTrainer"]}><CreateTryout userId={user?.id} /></PrivateRoute>} />
              <Route path="/admin/teams/:teamId/tryouts/:tryoutId/edit" element={<PrivateRoute roles={["Administrator", "SkillsTrainer"]}><EditTryout userId={user?.id} /></PrivateRoute>} />
              <Route path="/admin/teams/:teamId/tryouts/:tryoutId/athletes" element={<PrivateRoute roles={["Administrator", "SkillsTrainer"]}><RegisteredAthletes userId={user?.id} /></PrivateRoute>} />
              <Route path="/admin/swot" element={<PrivateRoute roles={["Administrator"]}><SkillsTrainerSwotHub /></PrivateRoute>} />
              <Route path="/admin/swot/:athleteId" element={<PrivateRoute roles={["Administrator"]}><SWOTPage /></PrivateRoute>} />

              {/* Trainer Routes */}
              <Route path="/skills-trainer" element={<PrivateRoute roles={["SkillsTrainer"]}><SkillsTrainerDashboard /></PrivateRoute>} />
              <Route path="/skills-trainer/profile" element={<PrivateRoute roles={["SkillsTrainer"]}><SkillsTrainerProfile userId={user?.id} /></PrivateRoute>} />
              <Route path="/skills-trainer/swot" element={<PrivateRoute roles={["SkillsTrainer"]}><SkillsTrainerSwotHub /></PrivateRoute>} />
              <Route path="/skills-trainer/swot/:athleteId" element={<PrivateRoute roles={["SkillsTrainer"]}><SWOTPage /></PrivateRoute>} />

              {/* Fallback */}
              <Route path="*" element={<Navigate to={getPublicEntryRoute()} replace />} />
            </Routes>
          </main>

          <InstallPrompt />
        </div>
      </Router>
    </ProgramProvider>
  );
}

export default App;

// RoleGuard Component (Unchanged)
function RoleGuard({ user, roles, children }) {
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRole = async () => {
      try {
        const { data, error } = await supabase
          .from("users")
          .select("role")
          .eq("id", user.id)
          .single();
        if (error) throw error;
        setUserRole(data.role);
      } catch (err) {
        console.error("Error fetching role:", err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchRole();
  }, [user]);

  if (loading) return <p className="p-8 text-center text-gray-500">Loading Access...</p>;
  if (!roles.includes(userRole)) return <Navigate to="/login" replace />;

  return children;
}
